import { supabase } from '../lib/supabase';
import { checkCurrentUserPermission } from '../utils/permissionEnforcement';

export const serviceConfigService = {
  // Service Categories
  async getAllCategories(tenantId) {
    try {
      let query = supabase?.from('service_categories')?.select('*');
      
      // Only filter by tenant_id if provided (Super Admins pass null to see all)
      if (tenantId) {
        query = query?.eq('tenant_id', tenantId);
      }
      
      const { data, error } = await query?.order('name');

      if (error) throw error;

      return data?.map(cat => ({
        id: cat?.id,
        name: cat?.name,
        description: cat?.description,
        color: cat?.color,
        icon: cat?.icon,
        isActive: cat?.is_active,
        tenantId: cat?.tenant_id,
        createdAt: cat?.created_at
      }));
    } catch (error) {
      console.error('Error fetching service categories:', error);
      throw error;
    }
  },

  async createCategory(category, tenantId) {
    try {
      const { data, error } = await supabase?.from('service_categories')?.insert({
          name: category?.name,
          description: category?.description,
          color: category?.color,
          icon: category?.icon,
          tenant_id: tenantId
        })?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        name: data?.name,
        description: data?.description,
        color: data?.color,
        icon: data?.icon,
        isActive: data?.is_active,
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Error creating service category:', error);
      throw error;
    }
  },

  async updateCategory(id, updates) {
    try {
      const { data, error } = await supabase?.from('service_categories')?.update({
          name: updates?.name,
          description: updates?.description,
          color: updates?.color,
          icon: updates?.icon
        })?.eq('id', id)?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        name: data?.name,
        description: data?.description,
        color: data?.color,
        icon: data?.icon,
        isActive: data?.is_active,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating service category:', error);
      throw error;
    }
  },

  async deleteCategory(id) {
    try {
      const { error } = await supabase?.from('service_categories')?.delete()?.eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting service category:', error);
      throw error;
    }
  },

  // Services
  async getAllServices(tenantId, filters = {}) {
    try {
      // Check permission dynamically
      const hasPermission = await checkCurrentUserPermission('services');
      if (!hasPermission) {
        throw new Error('Access denied: You do not have permission to view services');
      }

      let query = supabase?.from('services')?.select(`
          *,
          category:service_categories(id, name, color),
          staff:service_staff_assignments(
            staff:user_profiles(id, first_name, last_name, email)
          )
        `);
      
      // Only filter by tenant_id if provided (Super Admins pass null to see all)
      if (tenantId) {
        query = query?.eq('tenant_id', tenantId);
      }
      
      query = query?.order('name');

      if (filters?.categoryId) {
        query = query?.eq('category_id', filters?.categoryId);
      }

      if (filters?.isActive !== undefined) {
        query = query?.eq('is_active', filters?.isActive);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(service => ({
        id: service?.id,
        name: service?.name,
        description: service?.description,
        categoryId: service?.category_id,
        category: service?.category,
        duration: service?.duration,
        price: service?.price,
        bufferTime: service?.buffer_time,
        isActive: service?.is_active,
        onlineBookingEnabled: service?.online_booking_enabled,
        assignedStaff: service?.staff?.map(s => s?.staff),
        tenantId: service?.tenant_id,
        createdAt: service?.created_at,
        updatedAt: service?.updated_at
      }));
    } catch (error) {
      console.error('Error fetching services:', error);
      throw error;
    }
  },

  async getServiceById(id) {
    try {
      const { data, error } = await supabase?.from('services')?.select(`
          *,
          category:service_categories(id, name, color),
          staff:service_staff_assignments(
            staff:user_profiles(id, first_name, last_name, email)
          )
        `)?.eq('id', id)?.single();

      if (error) throw error;

      return {
        id: data?.id,
        name: data?.name,
        description: data?.description,
        categoryId: data?.category_id,
        category: data?.category,
        duration: data?.duration,
        price: data?.price,
        bufferTime: data?.buffer_time,
        isActive: data?.is_active,
        onlineBookingEnabled: data?.online_booking_enabled,
        assignedStaff: data?.staff?.map(s => s?.staff),
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error fetching service:', error);
      throw error;
    }
  },

  async createService(service, tenantId) {
    try {
      // Check permission dynamically
      const hasPermission = await checkCurrentUserPermission('services');
      if (!hasPermission) {
        throw new Error('Access denied: You do not have permission to create services');
      }

      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('services')?.insert({
          name: service?.name,
          description: service?.description,
          category_id: service?.categoryId,
          duration: service?.duration,
          price: service?.price,
          buffer_time: service?.bufferTime || 0,
          online_booking_enabled: service?.onlineBookingEnabled !== false,
          tenant_id: tenantId,
          created_by: user?.id
        })?.select()?.single();

      if (error) throw error;

      // Add staff assignments
      if (service?.staffIds && service?.staffIds?.length > 0) {
        await this.assignStaffToService(data?.id, service?.staffIds);
      }

      return {
        id: data?.id,
        name: data?.name,
        description: data?.description,
        categoryId: data?.category_id,
        duration: data?.duration,
        price: data?.price,
        bufferTime: data?.buffer_time,
        isActive: data?.is_active,
        onlineBookingEnabled: data?.online_booking_enabled,
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Error creating service:', error);
      throw error;
    }
  },

  async updateService(id, updates) {
    try {
      // Check permission dynamically
      const hasPermission = await checkCurrentUserPermission('services');
      if (!hasPermission) {
        throw new Error('Access denied: You do not have permission to update services');
      }

      const { data, error } = await supabase?.from('services')?.update({
          name: updates?.name,
          description: updates?.description,
          category_id: updates?.categoryId,
          duration: updates?.duration,
          price: updates?.price,
          buffer_time: updates?.bufferTime,
          is_active: updates?.isActive,
          online_booking_enabled: updates?.onlineBookingEnabled
        })?.eq('id', id)?.select()?.single();

      if (error) throw error;

      // Update staff assignments if provided
      if (updates?.staffIds) {
        // Remove existing assignments
        await supabase?.from('service_staff_assignments')?.delete()?.eq('service_id', id);

        // Add new assignments
        if (updates?.staffIds?.length > 0) {
          await this.assignStaffToService(id, updates?.staffIds);
        }
      }

      return {
        id: data?.id,
        name: data?.name,
        description: data?.description,
        categoryId: data?.category_id,
        duration: data?.duration,
        price: data?.price,
        bufferTime: data?.buffer_time,
        isActive: data?.is_active,
        onlineBookingEnabled: data?.online_booking_enabled,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating service:', error);
      throw error;
    }
  },

  async deleteService(id) {
    try {
      // Check permission dynamically
      const hasPermission = await checkCurrentUserPermission('services');
      if (!hasPermission) {
        throw new Error('Access denied: You do not have permission to delete services');
      }

      const { error } = await supabase?.from('services')?.delete()?.eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting service:', error);
      throw error;
    }
  },

  async assignStaffToService(serviceId, staffIds) {
    try {
      const assignments = staffIds?.map(staffId => ({
        service_id: serviceId,
        staff_id: staffId
      }));

      const { error } = await supabase?.from('service_staff_assignments')?.insert(assignments);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error assigning staff to service:', error);
      throw error;
    }
  },

  async bulkUpdateStatus(serviceIds, isActive) {
    try {
      const { error } = await supabase?.from('services')?.update({ is_active: isActive })?.in('id', serviceIds);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error bulk updating service status:', error);
      throw error;
    }
  }
};